import { useEffect } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { BookOpen, BookText, Settings, TrendingUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useActiveCompany } from "@/lib/company-context";
import { useJournalEntries, useAccountBalances } from "@/lib/api/journal.queries";

export const Route = createFileRoute("/companies/$companyId/contabilidad/")({
  component: ContabilidadIndexPage,
});

function ContabilidadIndexPage() {
  return (
    <RequireAuth>
      <AppShell>
        <ContabilidadIndexContent />
      </AppShell>
    </RequireAuth>
  );
}

function ContabilidadIndexContent() {
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const { data: entriesData, isLoading: loadingEntries, isError: errorEntries } = useJournalEntries(
    companyId,
    { pageSize: 5 },
  );
  const { data: balancesData, isLoading: loadingBalances } = useAccountBalances(companyId);

  const totalAccounts = balancesData?.accounts.length ?? 0;
  const totalEntries = entriesData?.total ?? 0;

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold">{t("nav.contabilidad.title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("journal.overview.subtitle")}
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <BookOpen className="h-4 w-4" />
              {t("nav.contabilidad.diario")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingEntries ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <span className="text-2xl font-bold">{totalEntries.toLocaleString("es-CO")}</span>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              {t("journal.overview.entriesCount")}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <BookText className="h-4 w-4" />
              {t("nav.contabilidad.mayor")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingBalances ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <span className="text-2xl font-bold">{totalAccounts.toLocaleString("es-CO")}</span>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              {t("journal.overview.accountsCount")}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Settings className="h-4 w-4" />
              {t("nav.contabilidad.cuentas")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {t("journal.overview.mappingsHint")}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Quick navigation */}
      <div className="flex flex-wrap gap-2">
        <Link
          to="/companies/$companyId/contabilidad/diario"
          params={{ companyId }}
          className="inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
        >
          <BookOpen className="h-4 w-4" />
          {t("journal.overview.viewDiario")}
        </Link>
        <Link
          to="/companies/$companyId/contabilidad/diario/nuevo"
          params={{ companyId }}
          className="inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
        >
          <TrendingUp className="h-4 w-4" />
          {t("journal.actions.newManualEntry")}
        </Link>
        <Link
          to="/companies/$companyId/contabilidad/mayor"
          params={{ companyId }}
          className="inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
        >
          <BookText className="h-4 w-4" />
          {t("journal.overview.viewMayor")}
        </Link>
      </div>

      {/* Recent entries */}
      {errorEntries ? (
        <ErrorState title={t("journal.error.loadEntries")} />
      ) : null}
    </div>
  );
}
